import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, CheckCircle2, ChevronDown, Clock, Download,
  Filter, PlayCircle, RefreshCw, RotateCcw, Search, XCircle,
} from "lucide-react";
import { api, API_ORIGIN } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";

const STATUS_STYLE = {
  success: { color: "var(--bull)", label: "Success", Icon: CheckCircle2 },
  running: { color: "var(--warn, #d19a66)", label: "Running", Icon: RefreshCw },
  failed: { color: "var(--bear)", label: "Failed", Icon: XCircle },
  disabled: { color: "var(--muted-3)", label: "Disabled", Icon: XCircle },
  waiting: { color: "var(--info, #4b8bbf)", label: "Waiting", Icon: Clock },
};

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
  } catch { return iso; }
};

const fmtDuration = (ms) => {
  if (ms == null) return "—";
  const n = Number(ms);
  if (n < 1000) return `${n.toFixed(0)} ms`;
  if (n < 60000) return `${(n / 1000).toFixed(2)} s`;
  return `${(n / 60000).toFixed(2)} min`;
};

const StatusPill = ({ status, disabled }) => {
  const effective = disabled ? "disabled" : (status || "waiting");
  const s = STATUS_STYLE[effective] || STATUS_STYLE.waiting;
  const Icon = s.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border"
      style={{ color: s.color, borderColor: s.color }}
      data-testid={`job-status-pill-${effective}`}
    >
      <Icon size={11} /> {s.label}
    </span>
  );
};

const Toggle = ({ checked, onChange, disabled, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors border"
    style={{
      backgroundColor: checked ? "rgba(74, 222, 128, 0.25)" : "rgba(148, 163, 184, 0.15)",
      borderColor: checked ? "var(--bull)" : "var(--muted-3)",
    }}
  >
    <span
      className="inline-block h-3.5 w-3.5 rounded-full transition-transform"
      style={{
        transform: checked ? "translateX(20px)" : "translateX(2px)",
        backgroundColor: checked ? "var(--bull)" : "var(--muted-2)",
      }}
    />
  </button>
);

const SummaryCard = ({ label, value, testid, color = "var(--muted-1)" }) => (
  <div
    className="border border-grid p-3 flex flex-col gap-1"
    data-testid={testid}
  >
    <div className="text-[9px] uppercase tracking-[0.25em] text-muted-3 font-mono-data">
      {label}
    </div>
    <div className="text-xl font-mono-data" style={{ color }}>
      {value ?? "—"}
    </div>
  </div>
);

const ErrorPopup = ({ error, onClose }) => {
  if (!error) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
      data-testid="job-error-popup"
    >
      <div
        className="bg-[#0b0f14] border max-w-2xl w-full mx-4 p-5 space-y-3"
        style={{ borderColor: "var(--bear)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={18} style={{ color: "var(--bear)" }} />
          <h3 className="font-heading text-base uppercase tracking-wider">
            Error · {error.job_name}
          </h3>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
          Timestamp: {fmtIST(error.started_at)}
        </div>
        <pre
          className="text-xs bg-black/50 p-3 overflow-auto max-h-[50vh] font-mono-data"
          style={{ color: "var(--bear)" }}
        >
          {error.message}
        </pre>
        {error.traceback && (
          <details data-testid="job-error-popup-traceback">
            <summary className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data cursor-pointer hover:text-white">
              Full Traceback
            </summary>
            <pre
              className="text-[10px] bg-black/50 p-3 mt-1 overflow-auto max-h-[30vh] font-mono-data"
              style={{ color: "var(--muted-2)" }}
            >
              {error.traceback}
            </pre>
          </details>
        )}
        <div className="text-[10px] text-muted-3 font-mono-data">
          Recommendation: Verify upstream service credentials and network health, then Retry
          this job.
        </div>
        <div className="flex justify-end">
          <button
            className="px-4 py-1.5 border text-[10px] uppercase tracking-[0.2em] font-mono-data"
            style={{ borderColor: "var(--muted-3)" }}
            onClick={onClose}
            data-testid="job-error-popup-close"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const JobDetailDrawer = ({ jobId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/jobs/${jobId}?limit=50`);
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load job detail");
    }
  }, [jobId]);
  useEffect(() => { if (jobId) load(); }, [jobId, load]);
  if (!jobId) return null;
  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black/60"
      onClick={onClose}
      data-testid="job-detail-drawer"
    >
      <div
        className="w-full sm:w-[560px] h-full bg-[#0b0f14] border-l border-grid overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {!detail ? (
          <div className="p-5 space-y-3">
            <Skeleton className="h-6 w-64" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[9px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
                  Job Detail
                </div>
                <h3 className="font-heading text-xl">{detail.job.name}</h3>
                <div className="text-xs text-muted-3 mt-1">{detail.job.description}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-[10px] uppercase tracking-wider font-mono-data border border-grid px-2 py-0.5">
                    {detail.job.category}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-mono-data border border-grid px-2 py-0.5">
                    {detail.job.schedule}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="job-detail-close"
                className="text-muted-3 hover:text-white"
              >
                <XCircle size={22} />
              </button>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-3 font-mono-data mb-2">
                Statistics · Today
              </div>
              <div className="grid grid-cols-2 gap-2">
                <SummaryCard label="Total Runs" value={detail.stats_today?.total} testid="stats-total" />
                <SummaryCard label="Success" value={detail.stats_today?.success} testid="stats-success" color="var(--bull)" />
                <SummaryCard label="Failed" value={detail.stats_today?.failed} testid="stats-failed" color="var(--bear)" />
                <SummaryCard label="Avg Duration" value={fmtDuration(detail.stats_today?.avg_duration_ms)} testid="stats-avg" />
                <SummaryCard label="Longest" value={fmtDuration(detail.stats_today?.longest_duration_ms)} testid="stats-longest" />
                <SummaryCard label="Matches" value={detail.stats_today?.matches} testid="stats-matches" />
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-3 font-mono-data mb-2">
                Last Successful Run
              </div>
              <div className="text-xs font-mono-data">
                {detail.last_success ? fmtIST(detail.last_success.started_at) : "—"}
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-3 font-mono-data mt-3 mb-2">
                Last Failed Run
              </div>
              <div className="text-xs font-mono-data">
                {detail.last_failed ? fmtIST(detail.last_failed.started_at) : "—"}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-3 font-mono-data">
                  Recent Runs (Last {detail.recent_runs?.length || 0})
                </div>
                <a
                  href={`${API_ORIGIN}/api/admin/jobs/${jobId}/export.csv`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-mono-data text-muted-1 hover:text-white"
                  data-testid="job-detail-export"
                >
                  <Download size={12} /> Export CSV
                </a>
              </div>
              <div className="border border-grid divide-y divide-[var(--grid)]">
                {(detail.recent_runs || []).map((r, idx) => (
                  <div key={idx} className="px-3 py-2 text-xs font-mono-data" data-testid={`job-detail-run-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <StatusPill status={r.status} />
                        <span className="text-muted-2">{fmtIST(r.started_at)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-muted-2">
                        <span>{fmtDuration(r.duration_ms)}</span>
                        {r.matches != null && <span>{r.matches} matches</span>}
                        {r.orders != null && r.orders > 0 && <span>{r.orders} orders</span>}
                        <span className="text-[9px] uppercase tracking-wider text-muted-3">{r.trigger}</span>
                      </div>
                    </div>
                    {/* Phase — surface this run's own error, not just the
                        latest run's. Previously a failed run buried in
                        history had no way to show *why* it failed here. */}
                    {r.status === "failed" && r.error && (
                      <div
                        className="mt-1 text-[10px] truncate"
                        style={{ color: "var(--bear)" }}
                        title={r.error}
                        data-testid={`job-detail-run-error-${idx}`}
                      >
                        {r.error}
                      </div>
                    )}
                    {r.details?.warnings?.length > 0 && (
                      <div
                        className="mt-1 text-[10px] truncate"
                        style={{ color: "var(--warn, #d19a66)" }}
                        title={r.details.warnings.join("; ")}
                        data-testid={`job-detail-run-warning-${idx}`}
                      >
                        ⚠ {r.details.warnings.join("; ")}
                      </div>
                    )}
                  </div>
                ))}
                {(!detail.recent_runs || detail.recent_runs.length === 0) && (
                  <div className="px-3 py-4 text-xs text-muted-3 text-center font-mono-data">
                    No runs recorded yet today.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const AdminJobs = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterEnabled, setFilterEnabled] = useState("");
  const [search, setSearch] = useState("");
  const [errorPopup, setErrorPopup] = useState(null);
  const [openDetailId, setOpenDetailId] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [busyJobId, setBusyJobId] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/jobs");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (refreshInterval > 0) {
      timerRef.current = setInterval(load, refreshInterval * 1000);
    }
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [refreshInterval, load]);

  const toggleJob = async (job) => {
    setBusyJobId(job.id);
    try {
      await api.post(`/admin/jobs/${job.id}/toggle`, { enabled: !job.enabled });
      toast.success(`${job.name} ${!job.enabled ? "enabled" : "disabled"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setBusyJobId(null);
    }
  };

  const runNow = async (job) => {
    setBusyJobId(job.id);
    toast.info(`${job.name} · Running now…`);
    try {
      const { data } = await api.post(`/admin/jobs/${job.id}/run-now`);
      if (data?.ok) toast.success(`${job.name} · Completed`);
      else toast.error(`${job.name} · ${data?.error || "Failed"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setBusyJobId(null);
    }
  };

  const retry = async (job) => {
    setBusyJobId(job.id);
    try {
      await api.post(`/admin/jobs/${job.id}/retry`);
      toast.success(`${job.name} · Retried`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusyJobId(null);
    }
  };

  const categories = useMemo(() => {
    const s = new Set();
    (data?.jobs || []).forEach((j) => s.add(j.category));
    return Array.from(s).sort();
  }, [data]);

  const filtered = useMemo(() => {
    return (data?.jobs || []).filter((j) => {
      if (filterCategory && j.category !== filterCategory) return false;
      const runStatus = j.enabled ? (j.last_run?.status || "waiting") : "disabled";
      if (filterStatus && runStatus !== filterStatus) return false;
      if (filterEnabled === "enabled" && !j.enabled) return false;
      if (filterEnabled === "disabled" && j.enabled) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(j.name.toLowerCase().includes(q) || j.id.toLowerCase().includes(q))) return false;
      }
      return true;
    });
  }, [data, filterCategory, filterStatus, filterEnabled, search]);

  const summary = data?.summary || {};

  return (
    <section className="space-y-5" data-testid="admin-jobs-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            Admin · Daily Jobs Dashboard
          </div>
          <h2 className="font-heading text-2xl flex items-center gap-2">
            <Activity size={20} /> Jobs & Scans Monitoring
          </h2>
          <p className="text-xs text-muted-3 mt-1">
            All scheduled scanners, background jobs and market-data pipelines in a single pane.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center gap-2">
            Refresh
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              data-testid="admin-jobs-refresh-interval"
              className="bg-transparent border border-grid px-2 py-1 text-xs font-mono-data"
            >
              <option value={0}>Off</option>
              <option value={10}>10 s</option>
              <option value={30}>30 s</option>
              <option value={60}>60 s</option>
            </select>
          </label>
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-jobs-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-grid text-[11px] uppercase tracking-[0.2em] font-mono-data hover:text-white"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        <SummaryCard label="Total Jobs" value={summary.total_jobs} testid="summary-total-jobs" />
        <SummaryCard label="Running" value={summary.running} testid="summary-running" color="var(--warn, #d19a66)" />
        <SummaryCard label="Success Today" value={summary.success_today} testid="summary-success" color="var(--bull)" />
        <SummaryCard label="Failed Today" value={summary.failed_today} testid="summary-failed" color="var(--bear)" />
        <SummaryCard label="Disabled" value={summary.disabled} testid="summary-disabled" color="var(--muted-3)" />
        <SummaryCard label="Matches Today" value={summary.matches_today} testid="summary-matches" />
        <SummaryCard label="Orders Today" value={summary.orders_today} testid="summary-orders" />
        <SummaryCard label="Avg Duration" value={fmtDuration(summary.avg_duration_ms)} testid="summary-avg-duration" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 border border-grid px-3 py-2">
        <Filter size={14} className="text-muted-3" />
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          data-testid="admin-jobs-filter-category"
          className="bg-transparent border border-grid px-2 py-1 text-xs"
        >
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="admin-jobs-filter-status"
          className="bg-transparent border border-grid px-2 py-1 text-xs"
        >
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="waiting">Waiting</option>
          <option value="disabled">Disabled</option>
        </select>
        <select
          value={filterEnabled}
          onChange={(e) => setFilterEnabled(e.target.value)}
          data-testid="admin-jobs-filter-enabled"
          className="bg-transparent border border-grid px-2 py-1 text-xs"
        >
          <option value="">Enabled + Disabled</option>
          <option value="enabled">Enabled only</option>
          <option value="disabled">Disabled only</option>
        </select>
        <div className="flex-1 min-w-[180px] flex items-center gap-1 border border-grid px-2 py-1">
          <Search size={12} className="text-muted-3" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search scan name…"
            data-testid="admin-jobs-search"
            className="bg-transparent flex-1 text-xs outline-none"
          />
        </div>
      </div>

      {/* Jobs table */}
      <div className="border border-grid overflow-x-auto" data-testid="admin-jobs-table">
        <table className="w-full text-xs font-mono-data">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-muted-3 border-b border-grid">
              <th className="text-left px-3 py-2">Scan</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2 max-w-[220px]">Schedule</th>
              <th className="text-left px-3 py-2">Last Run (IST)</th>
              <th className="text-right px-3 py-2">Duration</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Records</th>
              <th className="text-left px-3 py-2">Errors</th>
              <th className="text-center px-3 py-2">Enabled</th>
              <th className="text-center px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((j) => {
              const runStatus = j.enabled ? (j.last_run?.status || "waiting") : "disabled";
              const hasError = !!j.last_run?.error;
              return (
                <tr
                  key={j.id}
                  data-testid={`admin-jobs-row-${j.id}`}
                  className="border-b border-grid/40 hover:bg-white/[0.02]"
                >
                  <td className="px-3 py-2">
                    <button
                      onClick={() => setOpenDetailId(j.id)}
                      className="text-left hover:text-white"
                      data-testid={`admin-jobs-name-${j.id}`}
                    >
                      <div className="text-muted-1">{j.name}</div>
                      <div className="text-[10px] text-muted-3 mt-0.5">{j.description}</div>
                    </button>
                  </td>
                  <td className="px-3 py-2 text-muted-2">{j.category}</td>
                  <td className="px-3 py-2 text-muted-2 max-w-[220px] break-words">{j.schedule}</td>
                  <td className="px-3 py-2 text-muted-2 whitespace-nowrap">
                    {j.last_run ? fmtIST(j.last_run.started_at) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-muted-2 whitespace-nowrap">
                    {j.last_run ? fmtDuration(j.last_run.duration_ms) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={runStatus} disabled={!j.enabled} />
                  </td>
                  <td className="px-3 py-2 text-right text-muted-2 whitespace-nowrap">
                    {j.last_run ? (
                      <div className="text-[11px]">
                        {j.last_run.records != null && <div>{j.last_run.records} scanned</div>}
                        {j.last_run.matches != null && <div>{j.last_run.matches} matches</div>}
                        {j.last_run.orders != null && j.last_run.orders > 0 && <div>{j.last_run.orders} orders</div>}
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 max-w-[180px]">
                    {hasError ? (
                      <button
                        onClick={() => setErrorPopup({
                          job_name: j.name,
                          message: j.last_run.error,
                          started_at: j.last_run.started_at,
                          traceback: j.last_run?.details?.traceback,
                        })}
                        data-testid={`admin-jobs-error-${j.id}`}
                        className="text-[11px] underline"
                        style={{ color: "var(--bear)" }}
                      >
                        {String(j.last_run.error).slice(0, 40)}…
                      </button>
                    ) : (
                      <span className="text-muted-3">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Toggle
                      checked={j.enabled}
                      onChange={() => toggleJob(j)}
                      disabled={busyJobId === j.id}
                      testid={`admin-jobs-toggle-${j.id}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => runNow(j)}
                        disabled={busyJobId === j.id}
                        data-testid={`admin-jobs-runnow-${j.id}`}
                        title="Run Now"
                        className="p-1 border border-grid hover:text-white"
                      >
                        <PlayCircle size={13} />
                      </button>
                      {hasError && (
                        <button
                          onClick={() => retry(j)}
                          disabled={busyJobId === j.id}
                          data-testid={`admin-jobs-retry-${j.id}`}
                          title="Retry"
                          className="p-1 border border-grid hover:text-white"
                        >
                          <RotateCcw size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => setOpenDetailId(j.id)}
                        data-testid={`admin-jobs-detail-${j.id}`}
                        title="View Log"
                        className="p-1 border border-grid hover:text-white"
                      >
                        <ChevronDown size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-muted-3">
                  No jobs match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ErrorPopup error={errorPopup} onClose={() => setErrorPopup(null)} />
      <JobDetailDrawer jobId={openDetailId} onClose={() => setOpenDetailId(null)} />
    </section>
  );
};

export default AdminJobs;